import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, firstValueFrom, from, map } from 'rxjs';
import { QrCodeBuildingDto, QrCodeBuildingSectionDto, QrCodeService, QrSessionDto } from '@artemis-software/wr-api';
import { ContractorCredentialsService } from '@services/contractor-credentials.service';
import { QrSessionService } from '@services/qr-session.service';
import { asyncComputed } from '@/utils/signals/asyncComputed';

export type QrBuildingTab = '' | 'tga' | 'maintenance' | 'repairTasks';

export interface QrBuildingContext {
  readonly session: Signal<QrSessionDto | undefined>;
  readonly isExternal: Signal<boolean>;
  readonly routeBuildingId: Signal<string | null>;
  readonly buildingId: Signal<string | undefined>;
  readonly buildingInfo: Signal<QrCodeBuildingDto | undefined>;
  readonly sectionId: Signal<string | undefined>;
  readonly section: Signal<QrCodeBuildingSectionDto | undefined>;
  selectSection(sectionId: string): void;
  tabPath(tab: QrBuildingTab): string[];
  allBuildingsPath(): string[];
}

@Injectable({ providedIn: 'root' })
export class QrBuildingContextService {
  private readonly qrCodeService = inject(QrCodeService);
  private readonly credentials = inject(ContractorCredentialsService);
  private readonly selectedSections = signal<Record<string, string>>({});
  private cachedInfo?: { key: string; info: Promise<QrCodeBuildingDto> };

  buildingInfo(buildingId: string): Promise<QrCodeBuildingDto> {
    const key = `${this.credentials.getCurrentShortCode()}|${this.credentials.getCurrentPin()}|${buildingId}`;
    if (this.cachedInfo?.key !== key) {
      const info = firstValueFrom(this.qrCodeService.getBuildingInfo(buildingId));
      info.catch(() => this.cachedInfo = undefined);
      this.cachedInfo = { key, info };
    }
    return this.cachedInfo.info;
  }

  selectedSectionId(buildingId: string): string | undefined {
    return this.selectedSections()[buildingId];
  }

  selectSection(buildingId: string, sectionId: string): void {
    this.selectedSections.update(selected => ({ ...selected, [buildingId]: sectionId }));
  }

  shortCode(): string {
    return this.credentials.getCurrentShortCode() ?? '';
  }
}

export function qrBuildingContext(): QrBuildingContext {
  const route = inject(ActivatedRoute);
  const service = inject(QrBuildingContextService);
  const sessionService = inject(QrSessionService);

  const session = toSignal(from(sessionService.session().catch(() => undefined)));
  const routeBuildingId = toSignal(
    combineLatest(route.pathFromRoot.map(r => r.paramMap)).pipe(map(findBuildingId)),
    { initialValue: findBuildingId(route.pathFromRoot.map(r => r.snapshot.paramMap)) },
  );
  const isExternal = computed(() => session()?.kind === QrSessionDto.KindEnum.ExternalUser);
  const buildingId = computed(() => routeBuildingId() ?? session()?.buildingId);
  const buildingInfo = asyncComputed(() => {
    const id = buildingId();
    return id ? service.buildingInfo(id) : undefined;
  }, undefined);
  const sectionId = computed(() => {
    const current = session();
    const id = buildingId();
    if (!current || !id) {
      return undefined;
    }
    if (!isExternal()) {
      return current.buildingSectionId;
    }
    const info = buildingInfo();
    return service.selectedSectionId(id) ?? info?.scannedBuildingSectionId ?? info?.buildingSections[0]?.id;
  });
  const section = computed(() => buildingInfo()?.buildingSections.find(candidate => candidate.id === sectionId()));

  const basePath = (): string[] => {
    const id = routeBuildingId();
    const root = ['/building-qr', service.shortCode()];
    return id ? [...root, 'building', id] : root;
  };

  return {
    session,
    isExternal,
    routeBuildingId,
    buildingId,
    buildingInfo,
    sectionId,
    section,
    selectSection: (id: string) => {
      const building = buildingId();
      if (building) {
        service.selectSection(building, id);
      }
    },
    tabPath: (tab: QrBuildingTab) => (tab ? [...basePath(), tab] : basePath()),
    allBuildingsPath: () => ['/building-qr', service.shortCode(), 'external'],
  };
}

function findBuildingId(paramMaps: { get(name: string): string | null }[]): string | null {
  return paramMaps.map(params => params.get('buildingId')).find(id => !!id) ?? null;
}
